import { useEffect, useState } from 'react';
import { adminGetHealthAlerts, adminGetHealthSummary } from '../api/admin';
import { parseJsonSafe } from '../api/client';

function extractErrorMessage(data, fallback) {
  return (
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function formatStat(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
}

function truncJson(value, maxLen = 300) {
  if (value === null || value === undefined) {
    return '-';
  }
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  if (raw.length <= maxLen) {
    return raw;
  }
  return `${raw.slice(0, maxLen)}...`;
}

function getAlertNoticeClass(severity) {
  if (severity === 'CRITICAL') {
    return 'notice notice-critical';
  }
  if (severity === 'ERROR') {
    return 'notice notice-error';
  }
  if (severity === 'WARN') {
    return 'notice notice-warn';
  }
  if (severity === 'INFO') {
    return 'notice notice-info';
  }
  return 'notice';
}

function formatTopBucket(bucket) {
  if (!bucket || typeof bucket !== 'object' || !bucket.name) {
    return '-';
  }
  return `${bucket.name} (${formatStat(bucket.count)})`;
}

function getSubmissionTrustNoticeClass(status) {
  if (status === 'action_needed') {
    return 'notice notice-error';
  }
  if (status === 'watch') {
    return 'notice notice-warn';
  }
  if (status === 'no_issue') {
    return 'notice notice-info';
  }
  return 'notice';
}

function AdminHealthPage() {
  const [hours, setHours] = useState(24);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [data, setData] = useState(null);
  const [errorLevelFilter, setErrorLevelFilter] = useState('ALL');
  const [errorSearch, setErrorSearch] = useState('');
  const [alertsData, setAlertsData] = useState(null);
  const [alertsError, setAlertsError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadSummary = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setAlertsError('');
      try {
        const summaryResponse = await adminGetHealthSummary({ hours });
        const summaryPayload = await parseJsonSafe(summaryResponse);
        if (!summaryResponse.ok) {
          throw new Error(extractErrorMessage(summaryPayload, 'Failed to load health summary.'));
        }
        if (isMounted) {
          setData(summaryPayload || null);
        }

        try {
          const alertsResponse = await adminGetHealthAlerts({ hours });
          const alertsPayload = await parseJsonSafe(alertsResponse);
          if (!alertsResponse.ok) {
            throw new Error(extractErrorMessage(alertsPayload, 'Failed to load health alerts.'));
          }
          if (isMounted) {
            setAlertsData(alertsPayload || null);
          }
        } catch (alertsLoadError) {
          if (isMounted) {
            setAlertsData(null);
            setAlertsError(alertsLoadError.message || 'Failed to load health alerts.');
          }
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'Failed to load health summary.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSummary();
    return () => {
      isMounted = false;
    };
  }, [hours]);

  const totals = data?.totals || {};
  const eventCounts = totals?.event_counts_by_type || {};
  const errorCounts = totals?.error_counts_by_event || {};
  const timings = data?.timings || {};
  const nextQuestionTiming = timings?.next_question_duration_ms || timings?.next_question || {};
  const transcriptionTiming = timings?.transcription_done_duration_ms || timings?.transcription_done || {};
  const finalizeTiming = timings?.attempt_finalize_ms || timings?.attempt_finalize || {};
  const guardianBlockedCount = data?.guardian?.guardian_blocked_count;
  const recentErrors = Array.isArray(data?.recent_errors) ? data.recent_errors : [];
  const submissionTrustHealth = data?.submission_trust_health || {};
  const pageOcrHealth = data?.page_ocr_health || {};
  const overrideIntegrityHealth = data?.override_integrity_health || {};
  const confirmationHealth = data?.confirmation_health || {};
  const pageOcrReplayCandidates = Array.isArray(pageOcrHealth?.replay_handoff_candidates)
    ? pageOcrHealth.replay_handoff_candidates
    : [];
  const filteredRecentErrors = recentErrors.filter((item) => {
    if (errorLevelFilter !== 'ALL' && item?.level !== errorLevelFilter) {
      return false;
    }

    if (errorSearch.trim()) {
      const search = errorSearch.toLowerCase();
      const haystack = [
        item?.event,
        item?.level,
        item?.student_id,
        item?.session_id,
        item?.attempt_id,
        typeof item?.metadata === 'string'
          ? item.metadata
          : JSON.stringify(item?.metadata || {}),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    }

    return true;
  });
  const alerts = Array.isArray(alertsData?.alerts) ? alertsData.alerts : [];

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Admin workspace</p>
          <h1>Admin: System Health</h1>
          <p>Monitor alert severity, event volume, timings, guardian blocks, and recent runtime errors.</p>
        </div>
      </section>
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Window</p>
            <h2>Window</h2>
          </div>
        </div>
        <div className="stack-sm">
          <label htmlFor="health-hours">Lookback (hours)</label>
          <input
            id="health-hours"
            type="number"
            min="1"
            value={hours}
            onChange={(event) => {
              const parsed = Number(event.target.value);
              setHours(Number.isFinite(parsed) && parsed > 0 ? parsed : 24);
            }}
          />
          <p>window_start: {formatStat(data?.window_start)}</p>
          <p>window_end: {formatStat(data?.window_end)}</p>
        </div>
      </section>

      {isLoading ? <p>Loading health summary...</p> : null}
      {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}

      {!isLoading && !errorMessage ? (
        <>
          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Status</p>
                <h2>Alerts</h2>
              </div>
            </div>
            {alertsError ? <p className="muted-text">Alerts unavailable: {alertsError}</p> : null}
            {alerts.length === 0 ? (
              <p className="muted-text">No alerts.</p>
            ) : (
              <ul className="data-list">
                {alerts.map((item, index) => (
                  <li
                    key={`health-alert-${item?.code || index}`}
                    className={`data-list-item ${getAlertNoticeClass(item?.severity)}`}
                  >
                    <p>
                      [{formatStat(item?.severity)}] {formatStat(item?.code)}: {formatStat(item?.message)}
                    </p>
                    <p>
                      metric: {formatStat(item?.metric)} | value: {formatStat(item?.value)} | threshold:{' '}
                      {formatStat(item?.threshold)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Confirmation</p>
                <h2>Tutor Confirmed Evidence</h2>
              </div>
            </div>
            <div className={getSubmissionTrustNoticeClass(confirmationHealth?.attention_status)}>
              <p>attention_status: {formatStat(confirmationHealth?.attention_status)}</p>
              <p>{formatStat(confirmationHealth?.status_reason)}</p>
            </div>
            <p className="muted-text">
              Tutor confirmations are review signals only. They do not change score or progression.
            </p>
            <table>
              <thead>
                <tr>
                  <th>metric</th>
                  <th>value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>confirmation_count</td>
                  <td>{formatStat(confirmationHealth?.confirmation_count)}</td>
                </tr>
                <tr>
                  <td>confirmed_low_confidence_count</td>
                  <td>{formatStat(confirmationHealth?.confirmed_low_confidence_count)}</td>
                </tr>
                <tr>
                  <td>confirmation_after_override_count</td>
                  <td>{formatStat(confirmationHealth?.confirmation_after_override_count)}</td>
                </tr>
                <tr>
                  <td>confirmation_without_override_count</td>
                  <td>{formatStat(confirmationHealth?.confirmation_without_override_count)}</td>
                </tr>
                <tr>
                  <td>post_finalize_confirmation_count</td>
                  <td>{formatStat(confirmationHealth?.post_finalize_confirmation_count)}</td>
                </tr>
                <tr>
                  <td>top_confirmed_uncertainty_category</td>
                  <td>{formatTopBucket(confirmationHealth?.top_confirmed_uncertainty_category)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Override integrity</p>
                <h2>Tutor Override Evidence</h2>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>metric</th>
                  <th>value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>override_count</td>
                  <td>{formatStat(overrideIntegrityHealth?.override_count)}</td>
                </tr>
                <tr>
                  <td>override_on_low_confidence_count</td>
                  <td>{formatStat(overrideIntegrityHealth?.override_on_low_confidence_count)}</td>
                </tr>
                <tr>
                  <td>post_finalize_override_count</td>
                  <td>{formatStat(overrideIntegrityHealth?.post_finalize_override_count)}</td>
                </tr>
                <tr>
                  <td>top_evidence_integrity</td>
                  <td>{formatTopBucket(overrideIntegrityHealth?.top_evidence_integrity)}</td>
                </tr>
                <tr>
                  <td>top_override_uncertainty_category</td>
                  <td>{formatTopBucket(overrideIntegrityHealth?.top_uncertainty_category)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Page OCR</p>
                <h2>Page OCR Degradation</h2>
              </div>
            </div>
            <div className={getSubmissionTrustNoticeClass(pageOcrHealth?.attention_status)}>
              <p>attention_status: {formatStat(pageOcrHealth?.attention_status)}</p>
              <p>{formatStat(pageOcrHealth?.status_reason)}</p>
            </div>
            <table>
              <thead>
                <tr>
                  <th>metric</th>
                  <th>value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>degraded_submissions</td>
                  <td>{formatStat(pageOcrHealth?.degraded_submission_count)}</td>
                </tr>
                <tr>
                  <td>degraded_pages</td>
                  <td>{formatStat(pageOcrHealth?.degraded_page_count)}</td>
                </tr>
                <tr>
                  <td>top_degradation_bucket</td>
                  <td>{formatTopBucket(pageOcrHealth?.top_degradation_bucket)}</td>
                </tr>
              </tbody>
            </table>
            {pageOcrReplayCandidates.length > 0 ? (
              <div className="stack-sm">
                <h3>Repeated Page OCR Patterns</h3>
                <p className="muted-text">
                  Review these privacy-safe signatures for redacted replay fixture promotion.
                </p>
                <table>
                  <thead>
                    <tr>
                      <th>bucket</th>
                      <th>repeat_count</th>
                      <th>signature</th>
                      <th>action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageOcrReplayCandidates.map((candidate, index) => (
                      <tr key={candidate?.correlation_signature || `page-ocr-replay-${index}`}>
                        <td>{formatStat(candidate?.degradation_bucket)}</td>
                        <td>{formatStat(candidate?.repeat_count)}</td>
                        <td><code>{formatStat(candidate?.correlation_signature)}</code></td>
                        <td>{formatStat(candidate?.recommended_action)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Volume</p>
                <h2>Event Counts</h2>
              </div>
            </div>
            {Object.keys(eventCounts).length === 0 ? (
              <p className="muted-text">No event counts available.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>event_type</th>
                    <th>count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(eventCounts).map(([eventType, count]) => (
                    <tr key={eventType}>
                      <td>{eventType}</td>
                      <td>{formatStat(count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Failures</p>
                <h2>Error Counts By Event</h2>
              </div>
            </div>
            {Object.keys(errorCounts).length === 0 ? (
              <p className="muted-text">No error counts available.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>event</th>
                    <th>count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(errorCounts).map(([eventName, count]) => (
                    <tr key={eventName}>
                      <td>{eventName}</td>
                      <td>{formatStat(count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Submission trust</p>
                <h2>Submission Trust Health</h2>
              </div>
            </div>
            <div className={getSubmissionTrustNoticeClass(submissionTrustHealth?.attention_status)}>
              <p>attention_status: {formatStat(submissionTrustHealth?.attention_status)}</p>
              <p>{formatStat(submissionTrustHealth?.status_reason)}</p>
            </div>
            <table>
              <thead>
                <tr>
                  <th>metric</th>
                  <th>value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>recent_submit_failures</td>
                  <td>{formatStat(submissionTrustHealth?.submit_failure_count)}</td>
                </tr>
                <tr>
                  <td>top_submit_failure_bucket</td>
                  <td>{formatTopBucket(submissionTrustHealth?.top_submit_failure_bucket)}</td>
                </tr>
                <tr>
                  <td>low_confidence_marking_states</td>
                  <td>{formatStat(submissionTrustHealth?.low_confidence_mark_count)}</td>
                </tr>
                <tr>
                  <td>top_uncertainty_category</td>
                  <td>{formatTopBucket(submissionTrustHealth?.top_uncertainty_category)}</td>
                </tr>
                <tr>
                  <td>top_uncertainty_reason</td>
                  <td>{formatTopBucket(submissionTrustHealth?.top_uncertainty_reason)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Performance</p>
                <h2>Timings</h2>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>metric</th>
                  <th>avg</th>
                  <th>p50</th>
                  <th>p95</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>next_question</td>
                  <td>{formatStat(nextQuestionTiming?.avg)}</td>
                  <td>{formatStat(nextQuestionTiming?.p50)}</td>
                  <td>{formatStat(nextQuestionTiming?.p95)}</td>
                </tr>
                <tr>
                  <td>transcription_done</td>
                  <td>{formatStat(transcriptionTiming?.avg)}</td>
                  <td>{formatStat(transcriptionTiming?.p50)}</td>
                  <td>{formatStat(transcriptionTiming?.p95)}</td>
                </tr>
                <tr>
                  <td>attempt_finalize</td>
                  <td>{formatStat(finalizeTiming?.avg)}</td>
                  <td>{formatStat(finalizeTiming?.p50)}</td>
                  <td>{formatStat(finalizeTiming?.p95)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Safeguarding</p>
                <h2>Guardian</h2>
              </div>
            </div>
            <p>guardian_blocked_count: {formatStat(guardianBlockedCount)}</p>
          </section>

          <section className="card">
            <div className="section-header">
              <div>
                <p className="eyebrow">Diagnostics</p>
                <h2>Recent Errors</h2>
              </div>
            </div>
            <div className="meta-row">
              <div style={{ minWidth: 160 }}>
                <label htmlFor="error-level-filter">Level</label>
                <select
                  id="error-level-filter"
                  value={errorLevelFilter}
                  onChange={(e) => setErrorLevelFilter(e.target.value)}
                >
                  <option value="ALL">ALL</option>
                  <option value="CRITICAL">CRITICAL</option>
                  <option value="ERROR">ERROR</option>
                  <option value="WARN">WARN</option>
                </select>
              </div>

              <div style={{ flex: 1 }}>
                <label htmlFor="error-search">Search</label>
                <input
                  id="error-search"
                  type="text"
                  placeholder="Search event, student_id, metadata..."
                  value={errorSearch}
                  onChange={(e) => setErrorSearch(e.target.value)}
                />
              </div>
            </div>
            {filteredRecentErrors.length === 0 ? (
              recentErrors.length > 0 ? (
                <p className="muted-text">No results match current filters.</p>
              ) : (
                <p className="muted-text">No recent errors.</p>
              )
            ) : (
              <ul className="data-list">
                {filteredRecentErrors.map((item, index) => (
                  <li key={`recent-error-${item.created_at || index}`} className="data-list-item">
                    <p>created_at: {formatStat(item?.created_at)}</p>
                    <p>level: {formatStat(item?.level)}</p>
                    <p>event: {formatStat(item?.event)}</p>
                    <p>session_id: {formatStat(item?.session_id)}</p>
                    <p>student_id: {formatStat(item?.student_id)}</p>
                    <p>attempt_id: {formatStat(item?.attempt_id)}</p>
                    <p>metadata: <code>{truncJson(item?.metadata ?? item?.metadata_json)}</code></p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

export default AdminHealthPage;
